import BaseService from "../baseService";
import { IOptions } from "../../api-sdk";
import {
  IUpdateContentSource,
  IUpdateContentSourceResponse,
} from "./content.source.types";
import { AxiosResponse } from "axios";

class ContentSourceService extends BaseService {
  constructor(options: IOptions) {
    super(options);
  }

  /**
   * Update an existing content source.
   *
   * This method allows you to add or update a content source (video or trailer) for a specific piece of content.
   * You can link a source to content items (movies/series) and episodes with support for transcoding jobs.
   *
   * @param {IUpdateContentSource} payload - The content source update payload
   * @param {string} [payload.content_id] - The unique identifier of the content (movie/series) this source belongs to
   * @param {string} [payload.episode_id] - The unique identifier of the episode (for series content)
   * @param {string} payload.source_type - Type of source: "source" for main content or "trailer" for trailer content (required)
   * @param {string} payload.source_link - The URL/link to the video source or storage location (required)
   * @param {string} [payload.job_id] - The transcoding job ID associated with this source (if using transcoding service)
   *
   * @returns {Promise<AxiosResponse<IUpdateContentSourceResponse>>} Promise resolving to Axios response
   *   - response.data.id - The unique identifier of the created/updated source
   *   - response.data.message - Confirmation message from the server
   *
   * @throws {AxiosError} Throws error if:
   *   - Required fields are missing (400)
   *   - Content ID or episode ID not found (404)
   *   - Invalid source type provided (400)
   *   - Unauthorized access (401)
   *   - Server error (500)
   */
  async updateContentSource(
    payload: IUpdateContentSource,
  ): Promise<AxiosResponse<IUpdateContentSourceResponse>> {
    return this.request({
      method: "PATCH",
      url: "/api/v1/content/sources",
      data: payload,
    });
  }
}

export default ContentSourceService;
